import { Route, Routes } from 'react-router-dom';
import React, { lazy } from 'react';
import Main from './app/layout';
import { GuardedRoute } from './GuardedRoute';
import { CAMPAIGNS_READ, LOYALTY_READ, ORDERS_READ, PRODUCTS_READ, SHOPS_READ } from './app/shared/utils/permissions';
import { ProductMenusPage } from './app/views/ProductMenusPage';
import HolidaysPage from './app/views/HolidaysPage';
import OutOfStockPage from './app/views/OutOfStockProductsPage';
import ProductTimelinePage from './app/views/ProductTimelinePage/ProductTimelinePage';
import { MapContextProvider } from './app/views/DeliveryZonesPageV2/MapContext';
import PaymentMethodsExceptionsPage from './app/views/PaymentMethodsExceptionsPage/PaymentMethodsExceptionsPage';
import { OrderDetailsPage } from './app/views/OrderDetailsPage/OrderDetailsPage';
import GlobalExceptionHoursPage from './app/views/GlobalExceptionHoursPage/GlobalExceptionHoursPage';
import LoadingIndicator from './app/shared/LoadingIndicator';
import LegalEntitiesPage from './app/views/LegalEntitiesPage/LegalEntitiesPage';
import { UserRole } from '@kotipizzagroup/kotipizza-users-api-client';
import RestaurantOverviewPage from './app/views/RestaurantOverviewPage';
import { RestaurantOutOfStockView } from './app/views/OutOfStockProductsPage/RestaurantSpecific/RestaurantOutOfStockView';
import RestaurantOrdersPage from './app/views/RestaurantOrdersPage/RestaurantOrdersPage';

const MyRestaurantsPage = lazy(() => import('./app/views/MyRestaurantsPage'));
const CampaignsPage = lazy(() => import('./app/views/CampaignsPage'));
const ProfilePage = lazy(() => import('./app/views/ProfilePage'));
const CustomersPage = lazy(() => import('./app/views/CustomersPage'));
const OrdersPage = lazy(() => import('./app/views/OrdersPage/OrdersPage'));
const ProductsPage = lazy(() => import('./app/views/ProductsPage'));
const IngredientsPage = lazy(() => import('./app/views/IngredientsPage'));
const IngredientDosingsPage = lazy(() => import('./app/views/IngredientDosingsPage'));
const ProductsCategoriesPage = lazy(() => import('./app/views/ProductsCategoriesPage'));
const WarehouseItemsPage = lazy(() => import('./app/views/WarehouseItemsPage'));
const DeliveryZonePage = lazy(() => import('./app/views/DeliveryZonePage'));
const DeliveryZonesV2Page = lazy(() => import('./app/views/DeliveryZonesPageV2/DeliveryZonesPageV2'));
const RestaurantsPage = lazy(() => import('./app/views/RestaurantsPage'));
const SingleRestaurantPage = lazy(() => import('./app/views/SingleRestaurantPage'));
const EcomSalesReportPage = lazy(() => import('./app/views/EcomSalesReportPage/EcomSalesReportPage'));
const EcomLiveSalesReportPage = lazy(() => import('./app/views/EcomLiveSalesReportPage/EcomLiveSalesReportPage'));

const AppRoutes: React.FC = () => (
  <Main>
    <React.Suspense fallback={<LoadingIndicator />}>
      <Routes>
        <Route path="/" element={<MyRestaurantsPage />} />
        <Route path="/home" element={<MyRestaurantsPage />} />
        {/*
          Restaurant landing hub. `/:restaurant` is the bare-landing shortcut and `/:restaurant/home/`
          is what the nav (SideDrawer) navigates to — both render the same view. These are dynamic
          single-segment routes, so React Router ranks every static top-level route (/profile,
          /restaurants, /ecom-sales-report, …) above them; only genuinely-unknown single segments fall
          through here and render the "restaurant not found" state. Intentionally left unguarded so
          restaurant users can reach their own hub (guarding would redirect them to /home).
        */}
        <Route path="/:restaurant" element={<RestaurantOverviewPage />} />
        <Route path="/:restaurant/home/" element={<RestaurantOverviewPage />} />
        <Route path="/profile" element={<ProfilePage />} />
        <Route path="/holidays" element={<HolidaysPage />} />
        <Route element={<GuardedRoute requiredPermissions={[SHOPS_READ]} />}>
          <Route path="/global-exception-hours" element={<GlobalExceptionHoursPage />} />
        </Route>
        <Route element={<GuardedRoute requiredPermissions={[LOYALTY_READ]} />}>
          <Route path="/customers" element={<CustomersPage />} />
          <Route path="/customers/:id" element={<CustomersPage />} />
        </Route>
        {/* Which orders a restaurant user then sees is the orders API's decision, taken from the
            bearer token — not this client's. */}
        <Route
          element={
            <GuardedRoute requiredPermissions={[ORDERS_READ]} restaurantUserPermissions={[UserRole.Order_CanView]} />
          }
        >
          <Route path="/orders" element={<OrdersPage />} />
          <Route path="/orders/:id" element={<OrderDetailsPage />} />
          <Route path="/:restaurant/orders/" element={<RestaurantOrdersPage />} />
        </Route>
        <Route element={<GuardedRoute requiredPermissions={[CAMPAIGNS_READ]} />}>
          <Route path="/campaigns/*" element={<CampaignsPage />} />
        </Route>
        <Route element={<GuardedRoute requiredPermissions={[PRODUCTS_READ]} />}>
          <Route path="/products/*" element={<ProductsPage />} />
          <Route path="/upcoming-products" element={<ProductTimelinePage />} />
          <Route path="/ingredients/*" element={<IngredientsPage />} />
          <Route path="/ingredient-dosings/*" element={<IngredientDosingsPage />} />
          <Route path="/product-categories" element={<ProductsCategoriesPage />} />
          <Route path="/warehouse-items" element={<WarehouseItemsPage />} />
          <Route path="/product-menus/*" element={<ProductMenusPage />} />
        </Route>
        <Route
          element={
            <GuardedRoute
              requiredPermissions={[PRODUCTS_READ]}
              restaurantUserPermissions={[UserRole.OutOfStock_CanView]}
            />
          }
        >
          <Route path="/out-of-stock/*" element={<OutOfStockPage />} />
          {/* Editing an item happens in a dialog on the list itself, so there is no separate route. */}
          <Route path="/:restaurant/out-of-stock/" element={<RestaurantOutOfStockView />} />
        </Route>
        <Route element={<GuardedRoute requiredPermissions={[SHOPS_READ]} />}>
          <Route path="/legal-entities" element={<LegalEntitiesPage />} />
          <Route path="/restaurants/*" element={<RestaurantsPage />} />
          <Route path="/restaurants/area/:areaName" element={<RestaurantsPage />} />
          <Route path="/restaurants/:id" element={<SingleRestaurantPage />} />
          <Route path="/zones/*" element={<DeliveryZonePage />} />
          <Route
            path="/zones-v2/*"
            element={
              <MapContextProvider>
                <DeliveryZonesV2Page />
              </MapContextProvider>
            }
          />
          <Route path="/payment-methods-exceptions/area/:areaName" element={<PaymentMethodsExceptionsPage />} />
          <Route path="/payment-methods-exceptions/*" element={<PaymentMethodsExceptionsPage />} />
          <Route path="/ecom-sales-report" element={<EcomSalesReportPage />} />
          <Route path="/ecom-live-sales-report" element={<EcomLiveSalesReportPage />} />
        </Route>
      </Routes>
    </React.Suspense>
  </Main>
);

export default AppRoutes;
